import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';

import { axiosPrivate } from 'app/utils';

import type { PlusWebsitesResponse } from '../../../hooks/usePlusWebsites';

interface UpdateWebsiteAnalyticSettingsParams {
  websiteId: string;
  googleTagManagerId: string | null;
  googleMeasurementId: string | null;
}

function updateWebsiteAnalyticSettings({
  websiteId,
  googleTagManagerId,
  googleMeasurementId,
}: UpdateWebsiteAnalyticSettingsParams) {
  return axiosPrivate
    .put(`/v3/support/plus-websites/${websiteId}/analytic-settings`, {
      googleMeasurementId,
      googleTagManagerId,
    })
    .then((r) => r.data);
}

export function useUpdateWebsiteAnalyticSettings() {
  const queryClient = useQueryClient();
  const queryKey = ['plus-websites'];

  return useMutation({
    mutationFn: updateWebsiteAnalyticSettings,
    // Deliberately no invalidation after the write: the list endpoint can lag
    // behind this PUT, so an immediate refetch may return the old values and
    // overwrite this cache update (and refetchOnWindowFocus is disabled, so it
    // would stick until a remount). The accepted PUT is the source of truth.
    onSuccess: (
      _data,
      { websiteId, googleTagManagerId, googleMeasurementId }: UpdateWebsiteAnalyticSettingsParams
    ) => {
      queryClient.setQueriesData<AxiosResponse<PlusWebsitesResponse>>({ queryKey }, (old) =>
        old
          ? {
              ...old,
              data: {
                ...old.data,
                plusWebsites: old.data.plusWebsites.map((website) =>
                  website.id === websiteId
                    ? { ...website, googleTagManagerId, googleMeasurementId }
                    : website
                ),
              },
            }
          : old
      );
    },
    meta: {
      successMessage: 'Analytics settings updated successfully',
      errorMessage: 'Failed to update analytics settings',
    },
  });
}
